import { useState, useCallback, useEffect } from "react";
import { useCartStore } from "@/stores/cartStore";
import { cartQueue, type CartAction, type QueueMetrics } from "@/services";
import { toast } from "@/hooks/useToast";
import { logger } from "@/lib/logger";

interface UseCartQueueReturn {
  // 🎯 OPERACIONES DEL CARRO
  addItem: (item: any) => Promise<void>;
  removeItem: (cartItemId: string) => Promise<void>;
  updateQuantity: (cartItemId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;

  // 📊 ESTADO Y MÉTRICAS
  isProcessing: boolean;
  queueLength: number;
  metrics: QueueMetrics;
  pendingActions: CartAction[];
  currentActionId: string | null;

  // 🎯 UTILIDADES
  clearQueue: () => void;
  getQueueStatus: () => {
    isProcessing: boolean;
    queueLength: number;
    pendingActions: CartAction[];
    currentActionId: string | null;
  };
}

export function useCartQueue(): UseCartQueueReturn {
  const [isProcessing, setIsProcessing] = useState(false);
  const [queueLength, setQueueLength] = useState(0);
  const [currentActionId, setCurrentActionId] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<QueueMetrics>({
    totalActions: 0,
    successfulActions: 0,
    failedActions: 0,
    averageProcessingTime: 0,
    queueLength: 0,
  });
  const [pendingActions, setPendingActions] = useState<CartAction[]>([]);

  // 🎯 OBTENER ESTADO DEL CART STORE
  const {
    items,
    addAnonymousItem,

    clearCart: clearCartStore,
  } = useCartStore();

  // 🔄 ACTUALIZAR ESTADO DE LA COLA
  const updateQueueStatus = useCallback(() => {
    const status = cartQueue.getQueueStatus();
    setIsProcessing(status.isProcessing);
    setQueueLength(status.queueLength);
    setPendingActions(status.pendingActions);
    setCurrentActionId(status.currentActionId);
    setMetrics(cartQueue.getMetrics());
  }, []);

  // 🔄 MONITOREAR ESTADO DE LA COLA (suscripción en tiempo real)
  useEffect(() => {
    // Actualizar inmediatamente
    updateQueueStatus();

    // Suscribirse a cambios de la cola
    const unsubscribe = cartQueue.subscribe(() => {
      updateQueueStatus();
    });

    // Cleanup al desmontar
    return () => {
      unsubscribe();
    };
  }, [updateQueueStatus]);

  // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
  const updateUIOptimistic = useCallback(
    (action: CartAction) => {
      switch (action.type) {
        case "ADD_ITEM":
          const { item } = action.data;
          logger.log("🔑 adding item to cart", item);
          // addAnonymousItem(item); ELIMINADO PORQUE ENVIABA DUPLICADA LA SOLICITUD AL BACKEND

          // ACTUALIZAR UI
          useCartStore.setState((state) => {
            const existingItem = state.items.find((i) => i.id === item.id);
            const updatedItems = existingItem
              ? state.items.map((i) =>
                  i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
                )
              : [...state.items, { ...item, quantity: 1 }];
            return { items: updatedItems };
          });
          break;
        case "REMOVE_ITEM":
          // const { cartItemId } = action.data;
          // Encontrar el item por cartItemId y removerlo optimistamente
          // const itemToRemove = items.find(
          //   (item) => item.cartItemId?.toString() === cartItemId
          // );
          // if (itemToRemove) {
          //   // Actualizar el estado local optimistamente
          //   const updatedItems = items.filter(
          //     (item) => item.cartItemId?.toString() !== cartItemId
          //   );
          //   // Aquí necesitaríamos una forma de actualizar el estado del store
          //   // Por ahora, el optimistic update se maneja en el CartDrawer
          // }
          break;
        case "UPDATE_QUANTITY":
          // const { cartItemId: updateCartItemId, quantity } = action.data;
          // Encontrar el item por cartItemId y actualizar cantidad optimistamente
          // const itemToUpdate = items.find(
          //   (item) => item.cartItemId?.toString() === updateCartItemId
          // );
          // if (itemToUpdate) {
          //   // Actualizar el estado local optimistamente
          //   const updatedItems = items.map((item) =>
          //     item.cartItemId?.toString() === updateCartItemId
          //       ? { ...item, quantity }
          //       : item
          //   );
          //   // Aquí necesitaríamos una forma de actualizar el estado del store
          //   // Por ahora, el optimistic update se maneja en el CartDrawer
          // }
          break;
        case "CLEAR_CART":
          clearCartStore();
          break;
      }
    },
    [addAnonymousItem, items, clearCartStore]
  );


  // 🎯 AGREGAR ITEM AL CARRO
  const addItem = useCallback(
    async (item: any) => {
      try {
        // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
        updateUIOptimistic({
          id: "temp",
          type: "ADD_ITEM",
          data: { item },
          timestamp: Date.now(),
          retries: 0,
          maxRetries: 3,
          status: "PENDING",
        });

        // Agregar a la cola para procesamiento en background
        await cartQueue.addItem(item);

        // Mostrar toast de éxito
        toast({
          title: "¡Agregado al carrito!",
          description: `${item.name} se agregó exitosamente`,
          variant: "default",
        });
      } catch (error) {
        logger.error("Error agregando item al carro:", error);

        // Mostrar toast de error
        toast({
          title: "Error al agregar al carrito",
          description: "No se pudo agregar el producto. Inténtalo de nuevo.",
          variant: "destructive",
        });
      }
    },
    [updateUIOptimistic]
  );

  // 🎯 REMOVER ITEM DEL CARRO
  const removeItem = useCallback(
    async (cartItemId: string) => {
      try {
        // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
        updateUIOptimistic({
          id: "temp",
          type: "REMOVE_ITEM",
          data: { cartItemId },
          timestamp: Date.now(),
          retries: 0,
          maxRetries: 3,
          status: "PENDING",
        });

        // Agregar a la cola para procesamiento en background
        await cartQueue.removeItem(cartItemId);

        // Mostrar toast de éxito
        toast({
          title: "Producto removido",
          description: "El producto se removió del carrito",
          variant: "default",
        });
      } catch (error) {
        logger.error("Error removiendo item del carro:", error);

        // Mostrar toast de error
        toast({
          title: "Error al remover producto",
          description: "No se pudo remover el producto. Inténtalo de nuevo.",
          variant: "destructive",
        });
      }
    },
    [updateUIOptimistic]
  );

  // 🎯 ACTUALIZAR CANTIDAD
  const updateQuantity = useCallback(
    async (cartItemId: string, quantity: number) => {
      try {
        // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
        updateUIOptimistic({
          id: "temp",
          type: "UPDATE_QUANTITY",
          data: { cartItemId, quantity },
          timestamp: Date.now(),
          retries: 0,
          maxRetries: 3,
          status: "PENDING",
        });

        // Agregar a la cola para procesamiento en background
        await cartQueue.updateQuantity(cartItemId, quantity);

        // Mostrar toast de éxito
        toast({
          title: "Cantidad actualizada",
          description: "La cantidad se actualizó correctamente",
          variant: "default",
        });
      } catch (error) {
        logger.error("Error actualizando cantidad:", error);

        // Mostrar toast de error
        toast({
          title: "Error al actualizar cantidad",
          description: "No se pudo actualizar la cantidad. Inténtalo de nuevo.",
          variant: "destructive",
        });
      }
    },
    [updateUIOptimistic]
  );

  // 🎯 LIMPIAR CARRO
  const clearCart = useCallback(async () => {
    try {
      // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
      updateUIOptimistic({
        id: "temp",
        type: "CLEAR_CART",
        data: {},
        timestamp: Date.now(),
        retries: 0,
        maxRetries: 3,
        status: "PENDING",
      });

      // Agregar a la cola para procesamiento en background
      await cartQueue.clearCart();

      // Mostrar toast de éxito
      toast({
        title: "Carrito limpiado",
        description: "El carrito se limpió correctamente",
        variant: "default",
      });
    } catch (error) {
      logger.error("Error limpiando carro:", error);

      // Mostrar toast de error
      toast({
        title: "Error al limpiar carrito",
        description: "No se pudo limpiar el carrito. Inténtalo de nuevo.",
        variant: "destructive",
      });
    }
  }, [updateUIOptimistic]);

  // 🎯 LIMPIAR COLA
  const clearQueue = useCallback(() => {
    cartQueue.clearQueue();
    updateQueueStatus();
  }, [updateQueueStatus]);

  // 🎯 OBTENER ESTADO DE LA COLA
  const getQueueStatus = useCallback(() => {
    return cartQueue.getQueueStatus();
  }, []);

  return {
    // 🎯 OPERACIONES DEL CARRO
    addItem,
    removeItem,
    updateQuantity,
    clearCart,

    // 📊 ESTADO Y MÉTRICAS
    isProcessing,
    queueLength,
    metrics,
    pendingActions,
    currentActionId,

    // 🎯 UTILIDADES
    clearQueue,
    getQueueStatus,
  };
}



// 🎯 HOOK ESPECÍFICO PARA PRODUCTOS
export function useProductCartQueue() {
  const {
    addItem,
    isProcessing,
    queueLength,
    pendingActions,
    currentActionId,
  } = useCartQueue();

  const addProductToCart = useCallback(
    async (product: any) => {
      const cartItem = {
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        category: product.category,
        posId: product.posId,
      };

      await addItem(cartItem);
    },
    [addItem]
  );

  return {
    addProductToCart,
    isProcessing,
    queueLength,
    pendingActions,
    currentActionId,
  };
}
